import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/useAuthStore';
import { useNavigate } from 'react-router-dom';
import type { Database } from '../lib/database.types';
import { VehicleDetailsModal } from '../components/VehicleDetailsModal';
import { ServiceRequestReviewModal } from '../components/ServiceRequestReviewModal';
import { AlertCircle, Loader2 } from 'lucide-react';

type Car = Database['public']['Tables']['cars']['Row'] & {
  profiles: {
    id: string;
    first_name: string;
    last_name: string;
    username: string;
    email: string;
    phone: string | null;
    secondary_phone: string | null;
    avatar_url: string | null;
  } | null;
};

type ServiceRequest = Database['public']['Tables']['service_requests']['Row'] & {
  car: Car | null;
  profile: {
    id: string;
    first_name: string;
    last_name: string;
    username: string;
    email: string;
    phone: string | null;
    secondary_phone: string | null;
    avatar_url: string | null;
  } | null;
  images: Array<{ image_url: string }>;
  services: Array<{
    service: {
      id: string;
      name: string;
      category_id: string;
      category: {
        id: string;
        name: string;
      };
    };
  }>;
};

export function AdminDashboard() {
  const { user, isAdmin, loading: authLoading } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cars, setCars] = useState<Car[]>([]);
  const [serviceRequests, setServiceRequests] = useState<ServiceRequest[]>([]);
  const [selectedCar, setSelectedCar] = useState<Car | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<ServiceRequest | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  const fetchData = async () => {
    if (!user) return;
    setError(null);

    try {
      // Fetch cars with profile information
      const { data: carsData, error: carsError } = await supabase
        .from('cars')
        .select(`
          *,
          profiles!cars_profile_id_fkey (
            id,
            first_name,
            last_name,
            username,
            email,
            phone,
            secondary_phone,
            avatar_url
          )
        `)
        .order('created_at', { ascending: false });

      if (carsError) throw carsError;
      setCars(carsData || []);

      // Fetch service requests with all related data
      const { data: requestsData, error: requestsError } = await supabase
        .from('service_requests')
        .select(`
          *,
          car:cars (*),
          profile:profiles (
            id,
            first_name,
            last_name,
            username,
            email,
            phone,
            secondary_phone,
            avatar_url
          ),
          images:service_request_images (
            image_url
          ),
          services:service_request_services (
            service:services (
              id,
              name,
              category_id,
              category:service_categories (
                id,
                name
              )
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (requestsError) throw requestsError;
      setServiceRequests(requestsData || []);
      setError(null);
      setRetryCount(0);
    } catch (err) {
      console.error('Error fetching admin data:', err);
      
      if (retryCount < maxRetries) {
        setRetryCount(prev => prev + 1);
        setTimeout(fetchData, Math.min(1000 * Math.pow(2, retryCount), 5000));
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;

    if (!user || !isAdmin) {
      navigate('/');
      return;
    }

    fetchData();
  }, [user, isAdmin, authLoading, navigate]);

  const handleApproveVehicle = async (carId: string, details: { 
    brand: string; 
    model: string; 
    year: string; 
    chassisNumber: string;
    plateState: string;
    plateCode: string;
    plateNumber: string;
  }) => {
    try {
      const { error } = await supabase
        .from('cars')
        .update({
          brand: details.brand,
          model: details.model,
          year: parseInt(details.year),
          chassis_number: details.chassisNumber,
          plate_state: details.plateState,
          plate_code: details.plateCode,
          plate_number: details.plateNumber,
          status: 'approved'
        })
        .eq('id', carId);

      if (error) throw error;

      // Refresh data after update
      await fetchData();
      setSelectedCar(null);
    } catch (error) {
      console.error('Error approving vehicle:', error);
      setError(error instanceof Error ? error.message : 'Failed to approve vehicle');
    }
  };

  const handleRejectVehicle = async (carId: string, reason: string) => {
    try {
      const { error } = await supabase
        .from('cars')
        .update({
          status: 'rejected',
          rejection_reason: reason
        })
        .eq('id', carId);

      if (error) throw error;

      // Refresh data after update
      await fetchData();
      setSelectedCar(null);
    } catch (error) {
      console.error('Error rejecting vehicle:', error);
      setError(error instanceof Error ? error.message : 'Failed to reject vehicle');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'approved':
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    }
  };

  const renderUserInfo = (profile: Car['profiles'] | ServiceRequest['profile']) => {
    if (!profile) {
      return <span className="text-gray-400">User not found</span>;
    }

    return (
      <div className="flex items-start space-x-3">
        {profile.avatar_url ? (
          <img
            src={profile.avatar_url}
            alt={profile.username}
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white">
            {profile.first_name[0]}{profile.last_name[0]}
          </div>
        )}
        <div>
          <div className="font-medium text-gray-900 dark:text-white">
            {profile.first_name} {profile.last_name}
          </div>
          <div className="text-sm text-gray-500">@{profile.username}</div>
          <div className="text-sm text-gray-500">{profile.email}</div>
        </div>
      </div>
    );
  };

  const renderContactInfo = (profile: Car['profiles'] | ServiceRequest['profile']) => {
    if (!profile) return '-';

    return (
      <div className="space-y-1">
        {profile.phone && (
          <div className="text-gray-600 dark:text-gray-300">{profile.phone}</div>
        )}
        {profile.secondary_phone && (
          <div className="text-sm text-gray-500">
            Alt: {profile.secondary_phone}
          </div>
        )}
      </div>
    );
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <div className="text-lg text-gray-600 dark:text-gray-300">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
          Admin Dashboard
        </h1>

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <div>
              <p className="font-medium">Error loading data</p>
              <p className="text-sm">{error}</p>
            </div>
            <button
              onClick={fetchData}
              className="ml-auto px-3 py-1 text-sm bg-red-200 hover:bg-red-300 rounded-md transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Cars Section */}
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Vehicle Registrations
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b dark:border-gray-600">
                  <th className="pb-3 text-gray-900 dark:text-white">Submission Date</th>
                  <th className="pb-3 text-gray-900 dark:text-white">Owner</th>
                  <th className="pb-3 text-gray-900 dark:text-white">Contact</th>
                  <th className="pb-3 text-gray-900 dark:text-white">Status</th>
                  <th className="pb-3 text-gray-900 dark:text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                {cars.map((car) => (
                  <tr key={car.id} className="border-b dark:border-gray-600">
                    <td className="py-3 text-gray-600 dark:text-gray-300">
                      {formatDate(car.created_at)}
                    </td>
                    <td className="py-3">
                      {renderUserInfo(car.profiles)}
                    </td>
                    <td className="py-3">
                      {renderContactInfo(car.profiles)}
                    </td>
                    <td className="py-3">
                      <span className={`capitalize px-2 py-1 rounded text-sm ${getStatusBadgeClass(car.status)}`}>
                        {car.status}
                      </span>
                      {car.rejection_reason && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                          Reason: {car.rejection_reason}
                        </p>
                      )}
                    </td>
                    <td className="py-3">
                      {car.status === 'pending' && (
                        <button
                          onClick={() => setSelectedCar(car)}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Review
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Service Requests Section */}
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Service Requests
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b dark:border-gray-600">
                  <th className="pb-3 text-gray-900 dark:text-white">Date</th>
                  <th className="pb-3 text-gray-900 dark:text-white">Customer</th>
                  <th className="pb-3 text-gray-900 dark:text-white">Vehicle</th>
                  <th className="pb-3 text-gray-900 dark:text-white">Status</th>
                  <th className="pb-3 text-gray-900 dark:text-white">Quote</th>
                  <th className="pb-3 text-gray-900 dark:text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                {serviceRequests.map((request) => (
                  <tr key={request.id} className="border-b dark:border-gray-600">
                    <td className="py-3 text-gray-600 dark:text-gray-300">
                      {formatDate(request.created_at)}
                    </td>
                    <td className="py-3">
                      {renderUserInfo(request.profile)}
                    </td>
                    <td className="py-3 text-gray-600 dark:text-gray-300">
                      {request.car ? (
                        `${request.car.brand} ${request.car.model}`
                      ) : (
                        <span className="text-gray-400">Vehicle not found</span>
                      )}
                    </td>
                    <td className="py-3">
                      <span className={`capitalize px-2 py-1 rounded text-sm ${getStatusBadgeClass(request.status)}`}>
                        {request.status}
                      </span>
                      {request.rejection_reason && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                          Reason: {request.rejection_reason}
                        </p>
                      )}
                    </td>
                    <td className="py-3 text-gray-600 dark:text-gray-300">
                      {request.quote_amount ? `AED ${request.quote_amount}` : '-'}
                    </td>
                    <td className="py-3">
                      <button
                        onClick={() => setSelectedRequest(request)}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Vehicle Details Modal */}
      {selectedCar && (
        <VehicleDetailsModal
          car={selectedCar}
          onClose={() => setSelectedCar(null)}
          onApprove={handleApproveVehicle}
          onReject={handleRejectVehicle}
        />
      )}

      {/* Service Request Review Modal */}
      {selectedRequest && (
        <ServiceRequestReviewModal
          isOpen={true}
          onClose={() => setSelectedRequest(null)}
          onRequestUpdated={fetchData}
          request={selectedRequest}
        />
      )}
    </div>
  );
}